<template>
  <div class="creation-demand-step-planning-shifts">
    <ExtraTipbox v-if="totalShiftsOverMaxShifts" class="q-mb-md">
      <span>{{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@LONG_DEMAND")) }}</span>
      <br />
      <a @click="contactEtabSupport">
        <QIcon name="icon-f-mail" />
        <span>{{ $t("ETAB.PAGES.DEMANDS.CREATION.@LONG_DEMAND_CONTACT") }}</span>
      </a>
    </ExtraTipbox>
    <div v-if="!isToday(internalPlanning.getDays()[0].getId())" class="row justify-center q-mb-xs">
      <QBtn round icon="fa fa-plus" size="sm" @click="addPriorSchedule()" />
    </div>
    <div v-for="day in internalPlanning.getDays()" :key="day.getId()" class="creation-planning-item row justify-between">
      <section class="column justify-around items-center" :class="!isSelected(day.getId()) ? 'text-grey' : ''">
        <span class="text-bold">{{ getDayLabel(day) }}</span>
        <IosToggle size="sm" :model-value="isSelected(day.getId())" @update:model-value="toggleDay(day)" />
      </section>
      <Schedule
        v-if="isSelected(day.getId())"
        class="schedule-box"
        :hide-icon="$q.screen.lt.md"
        :is-today="isToday(day.getId())"
        :start-time="getPlanningDay(day.getId())[ICreationStepPlanningDay.start]"
        :end-time="getPlanningDay(day.getId())[ICreationStepPlanningDay.end]"
        :break-time-duration="getPlanningDay(day.getId())[ICreationStepPlanningDay.break]"
        :schedule-options="getScheduleOptions(day)"
        @select-start="setSchedule({ dayId: day.getId(), property: ICreationStepPlanningDay.start, value: $event })"
        @select-end="setSchedule({ dayId: day.getId(), property: ICreationStepPlanningDay.end, value: $event })"
        @select-break-duration="setSchedule({ dayId: day.getId(), property: ICreationStepPlanningDay.break, value: $event })"
      />
      <IconBoxWrapper v-if="!isSelected(day.getId())" icon="icon-f-clock" icon-color="white" class="creation-planning-disabled schedule-box">
        <section>{{ $t("ETAB.PAGES.DEMANDS.CREATION.@PLANNING_DISABLED") }}</section>
      </IconBoxWrapper>
    </div>
    <div class="row justify-center q-mt-xs">
      <QBtn round icon="fa fa-plus" size="sm" @click="addAfterSchedule()" />
    </div>
    <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
      <section class="q-footer q-layout__section--marginal fixed-bottom">
        <div>
          <QBtn
            color="secondary"
            class="full-width btn-navigation"
            :label="buttonLabel"
            no-caps
            :disabled="!planning.isValid() || totalShiftsOverMaxShifts"
            :loading="loading"
            @click.once="save"
          />
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
  import { DatesHelpers, ScheduleHelpers } from "@core/helpers";
  import { CreationStepPlanningModel, ICreationStepDates, ICreationStepPlanningDay } from "@core/models";
  import { EtabProfileService } from "@core/services";
  import { ContactManager } from "@core/managers/contact.manager";
  import { API_DATE_FORMAT_SHORT, API_OFFER_CREATION_MAX_SHIFTS } from "@core/constants";

  import Schedule from "@core/components/forms/Schedule.component.vue";

  export default {
    components: {
      Schedule,
    },
    props: {
      planning: {
        type: Object,
        default: new CreationStepPlanningModel(),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saveStep", "updateDates", "update:planning"],
    computed: {
      totalShiftsOverMaxShifts() {
        return this.internalPlanning.totalSelectedDays() > API_OFFER_CREATION_MAX_SHIFTS;
      },
      dates() {
        const date = this.planning.getDates();
        if (date && date.start && date.end) {
          return { start: date.start, end: date.end };
        }
        return date;
      },
      buttonLabel() {
        const durationInMinutes = this.planning.getTotalDurationInMinutes();
        if (durationInMinutes >= 30) {
          const duration = DatesHelpers.formatMinsToHrsMins(durationInMinutes);
          return this.$t("ETAB.PAGES.DEMANDS.CREATION.@PLANNING_CONFIRM_DURATION", { duration });
        }
        return this.$t("ETAB.PAGES.DEMANDS.CREATION.@PLANNING_CONFIRM");
      },
      internalPlanning: {
        get() {
          return this.planning;
        },
        set(newVal) {
          this.$emit("update:planning", newVal);
        },
      },
    },
    created() {
      // we clone in order to avoid the planning days being copied by reference
      this.ICreationStepPlanningDay = ICreationStepPlanningDay;
      this.API_OFFER_CREATION_MAX_SHIFTS = API_OFFER_CREATION_MAX_SHIFTS;
      this.internalPlanning.setPlanningDays();
    },
    methods: {
      async contactEtabSupport() {
        const etabId = await EtabProfileService.getEtabId();
        const subject = this.$t("EMAILS.@LONG_DEMAND_ETAB", { id: etabId });
        ContactManager.contactEtabSupport({ subject });
      },
      isSelected(id) {
        return this.internalPlanning.isDaySelected(id);
      },
      getPlanningDay(dayId) {
        return this.internalPlanning.getDays().find((d) => d.id === dayId);
      },
      toggleDay(day) {
        this.internalPlanning.toggleDay(day.id, false);
      },
      setSchedule({ dayId, property, value }) {
        this.internalPlanning.scheduleDay({ dayId, property, value });
      },
      getScheduleOptions(day) {
        return ScheduleHelpers.getFullScheduleOptions({
          undefined,
          stampText: this.$t("CORE.COMPONENTS.SCHEDULE.@DAY_PLUS_ONE"),
          nullBreakLabel: this.$t("CORE.COMPONENTS.SCHEDULE.@BREAK_NONE"),
        });
      },
      getDayLabel(day = {}) {
        return DatesHelpers.getDayjs(day.getDate()).format("ddd DD/MM");
      },
      toggleAllowed(index) {
        // prevent toggling first and last day
        return index !== 0 && index !== this.internalPlanning.getDays().length - 1;
      },
      isToday(dayId) {
        return this.getPlanningDay(dayId).isToday();
      },
      addPriorSchedule() {
        const { start, end } = this.dates;
        this.internalPlanning.setDates({
          start: DatesHelpers.getDayjs(start).subtract(1, "day").format(API_DATE_FORMAT_SHORT),
          end,
        });
        this.extendDates(this.internalPlanning.getAPIFormattedDates());
      },
      addAfterSchedule() {
        const { start, end } = this.dates;
        this.internalPlanning.setDates({ start, end: DatesHelpers.getDayjs(end).add(1, "day").format(API_DATE_FORMAT_SHORT) });
        this.extendDates(this.internalPlanning.getAPIFormattedDates());
      },
      extendDates({ start, end }) {
        this.$emit("updateDates", {
          dates: {
            [ICreationStepDates.startDate]: start,
            [ICreationStepDates.endDate]: end,
          },
        });
      },
      save() {
        this.$emit("saveStep");
      },
    },
  };
</script>

<style lang="scss">
  .creation-demand-step-planning-shifts {
    .schedule-box {
      width: 305px;
      @media screen and (max-width: $breakpoint-sm-max) {
        width: 260px;
      }
    }

    .creation-planning-item {
      padding: 5px 0;
      box-sizing: border-box;

      > section {
        flex: 1;
        @media screen and (max-width: $breakpoint-sm-max) {
          margin-right: 10px;
        }
      }
    }

    .creation-planning-disabled {
      section {
        display: flex;
        align-items: center;
        padding: 5px 15px;
        color: #979797;
      }
    }
  }
</style>
